import { useEffect, useMemo, useRef, useState } from "react";

export function InfoChip({ title, value, sku = false }) {
  return (
    <div className={`pfm-chip${sku ? " pfm-chip--sku" : ""}`}>
      <span className="pfm-chip__label">{title}</span>
      <strong className="pfm-chip__value">{value}</strong>
    </div>
  );
}

export function SummaryRow({ label, value }) {
  return (
    <div className="pfm-summary-row">
      <span className="pfm-summary-row__label">{label}</span>
      <span className="pfm-summary-row__value">{value}</span>
    </div>
  );
}

export function ToolbarButton({ children, ...props }) {
  return (
    <button type="button" {...props} className="pfm-editor__tool">
      {children}
    </button>
  );
}

export function ToolbarDivider() {
  return <div className="pfm-editor__divider" aria-hidden />;
}

export function Input({ label, ...props }) {
  return (
    <div className="pfm-field">
      <label className="pfm-label">{label}</label>
      <input {...props} className="pfm-input" />
    </div>
  );
}

export function SearchSelect({
  label,
  name,
  value,
  onChange,
  disabled,
  options,
  placeholder = "Selecciona...",
  searchPlaceholder = "Escribe para buscar...",
  maxListHeight = 240,
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const wrapRef = useRef(null);

  const selected = useMemo(() => {
    return options.find((o) => String(o.value) === String(value));
  }, [options, value]);

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    if (!query) return options;
    return options.filter((o) => String(o.label || "").toLowerCase().includes(query));
  }, [options, q]);

  useEffect(() => {
    if (!open) return;

    const onDocMouseDown = (e) => {
      if (!wrapRef.current) return;
      if (!wrapRef.current.contains(e.target)) setOpen(false);
    };

    const onEsc = (e) => {
      if (e.key === "Escape") setOpen(false);
    };

    document.addEventListener("mousedown", onDocMouseDown);
    window.addEventListener("keydown", onEsc);

    return () => {
      document.removeEventListener("mousedown", onDocMouseDown);
      window.removeEventListener("keydown", onEsc);
    };
  }, [open]);

  const pick = (opt) => {
    onChange?.({ target: { name, value: opt.value } });
    setOpen(false);
    setQ("");
  };

  return (
    <div className="pfm-field pfm-field--select" ref={wrapRef}>
      <label className="pfm-label">{label}</label>

      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((s) => !s)}
        className={`pfm-select-trigger${open ? " is-open" : ""}${
          selected ? " has-value" : ""
        }`}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className={selected ? "pfm-select-trigger__value" : "pfm-select-trigger__placeholder"}>
          {selected ? selected.label : placeholder}
        </span>
        <span className="pfm-select-trigger__chevron" aria-hidden>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
            <path d="M6 9l6 6 6-6" />
          </svg>
        </span>
      </button>

      {open && !disabled && (
        <div className="pfm-select-dropdown" role="listbox">
          <div className="pfm-select-dropdown__search-wrap">
            <svg className="pfm-select-dropdown__search-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              <circle cx="11" cy="11" r="7" />
              <path d="m21 21-4.3-4.3" />
            </svg>
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={searchPlaceholder}
              className="pfm-select-dropdown__search"
              autoFocus
            />
          </div>

          <div className="pfm-select-dropdown__list" style={{ maxHeight: maxListHeight }}>
            {filtered.length === 0 ? (
              <div className="pfm-select-dropdown__empty">Sin resultados</div>
            ) : (
              filtered.map((opt) => {
                const active = String(opt.value) === String(value);

                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => pick(opt)}
                    className={`pfm-select-dropdown__item${
                      active ? " pfm-select-dropdown__item--active" : ""
                    }`}
                    role="option"
                    aria-selected={active}
                    title={opt.label}
                  >
                    <span className="pfm-select-dropdown__item-label">{opt.label}</span>
                    {active && (
                      <svg className="pfm-select-dropdown__item-check" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                        <path d="M20 6 9 17l-5-5" />
                      </svg>
                    )}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
